import { ApolloError } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import {
  BenchTenderShape,
  useCreateOutboundBenchOrdersMutation,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";
import { type BenchOrderOption } from "../bench-run";
import {
  defaultSchedule,
  scheduleInput,
  type BenchOrderSchedule,
} from "../create-orders-schedule";
import CreateOrdersScheduleFields from "./create-orders-schedule-fields";

const styles = {
  controls: {
    display: "flex",
    gap: 2,
    alignItems: "flex-start",
    flexWrap: "wrap",
  } satisfies SxProps<Theme>,
  count: { width: 140 } satisfies SxProps<Theme>,
  shape: { minWidth: 220 } satisfies SxProps<Theme>,
  // Matches the outlined TextField height, so the button lines up with the
  // fields while their helper text hangs below.
  createButton: { height: 56 } satisfies SxProps<Theme>,
};

/** The backend's cap on one call (`MAX_BENCH_CREATED_ORDERS`). */
export const MAX_CREATED_ORDERS = 10;

const SHAPE_LABELS: Record<BenchTenderShape, string> = {
  [BenchTenderShape.PickupAndDelivery]: "Pickup and delivery",
  [BenchTenderShape.Pickup]: "Pickup only",
  [BenchTenderShape.Delivery]: "Delivery only",
};

const SHAPES = Object.values(BenchTenderShape);

/** The count to send, or null when the field does not hold 1 to 10. */
export const parseCreateCount = (text: string): number | null => {
  const count = Number(text);
  return Number.isInteger(count) && count >= 1 && count <= MAX_CREATED_ORDERS
    ? count
    : null;
};

/** The success line, naming the rated order whose lane the tenders copied. */
export const createdMessage = (
  count: number,
  names: string,
  laneSourceOrderName: string | null,
): string =>
  laneSourceOrderName === null
    ? `Created ${count} order(s): ${names}.`
    : `Created ${count} order(s): ${names}, on the lane of rated order ${laneSourceOrderName}.`;

/** Names the service the created orders received, when any. */
export const serviceNote = (receivedServiceNames: readonly string[]): string =>
  receivedServiceNames.length === 0
    ? ""
    : ` Service: ${receivedServiceNames.join(", ")}.`;

/**
 * Appended when the create call fails in transport. The backend creates the
 * tenders one at a time, so a timeout or dropped connection can arrive after
 * some orders exist. A GraphQL error carries the backend's own answer instead.
 */
export const PARTIAL_CREATE_HINT =
  "Some orders may have been created before the error. Search Orders for BENCH- before you try again.";

/** The acknowledgement the operator ticks before creating in production. */
export const productionAcknowledgement = ({
  count,
  companyName,
  contactName,
}: {
  count: number | null;
  companyName: string | null;
  contactName: string | null;
}): string =>
  `I understand this creates ${count ?? "?"} real order(s) in ${
    companyName ?? "the company"
  } for ${contactName ?? "the customer"} in production.`;

/**
 * Whether Create may be pressed. Outside production it needs a partnership
 * and a valid count. In production it also needs the typed `prod`
 * (`confirmProductionSend`) and the acknowledgement ticked for this same
 * partnership and count, so changing either clears it.
 */
export const canCreateOrders = ({
  partnershipUuid,
  count,
  production,
  confirmProductionSend,
  acknowledgedKey,
}: {
  partnershipUuid: string | null;
  count: number | null;
  production: boolean;
  confirmProductionSend: boolean | undefined;
  acknowledgedKey: string | null;
}): boolean =>
  partnershipUuid !== null &&
  count !== null &&
  (!production ||
    (confirmProductionSend === true &&
      acknowledgedKey === acknowledgementKey(partnershipUuid, count)));

/** What the acknowledgement was ticked for. */
export const acknowledgementKey = (
  partnershipUuid: string,
  count: number,
): string => `${partnershipUuid}:${count}`;

type CreateOrdersSectionProps = {
  readonly partnershipUuid: string | null;
  readonly companyName: string | null;
  readonly contactName: string | null;
  readonly production: boolean;
  /** `true` once the operator has typed `prod` against production. */
  readonly confirmProductionSend: boolean | undefined;
  readonly onCreated: (orders: BenchOrderOption[]) => void;
};

/**
 * Creates new orders for the selected partnership by sending generated 204
 * tenders through the EDI inbound handler, and hands them to the order
 * picker. The tenders copy the lane of the contact's newest rated order; when
 * it has none, the page shows the backend's warning that the orders may rate
 * at $0.
 *
 * Against production the button stays disabled until the operator has typed
 * `prod` and ticked the acknowledgement, and pressing it opens a dialog whose
 * default action is Cancel. Only Confirm sends the two production opt-ins.
 */
const CreateOrdersSection = ({
  partnershipUuid,
  companyName,
  contactName,
  production,
  confirmProductionSend,
  onCreated,
}: CreateOrdersSectionProps) => {
  const [countText, setCountText] = useState("1");
  const [shape, setShape] = useState(BenchTenderShape.PickupAndDelivery);
  const [schedule, setSchedule] = useState<BenchOrderSchedule>(() =>
    defaultSchedule(new Date()),
  );
  const [message, setMessage] = useState<string | null>(null);
  const [staleNotice, setStaleNotice] = useState<string | null>(null);
  const [laneWarning, setLaneWarning] = useState<string | null>(null);
  const [sideEffects, setSideEffects] = useState<string[]>([]);
  const [acknowledgedKey, setAcknowledgedKey] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOrders, { loading }] = useCreateOutboundBenchOrdersMutation();
  const count = parseCreateCount(countText);
  // The partnership on screen now, read when a create call returns: orders
  // made for one the operator has since left must stay out of the selection,
  // or the next run acts on them while the page shows another partnership.
  const currentPartnershipRef = useRef(partnershipUuid);
  useEffect(() => {
    currentPartnershipRef.current = partnershipUuid;
  }, [partnershipUuid]);
  // The service-level list belongs to one partnership. A code kept across a
  // switch has no option in the new list, and the backend refuses it.
  useEffect(() => {
    setSchedule((current) =>
      current.serviceLevelCode === ""
        ? current
        : { ...current, serviceLevelCode: "" },
    );
  }, [partnershipUuid]);
  // Set from the first click until the call settles. The confirm dialog stays
  // clickable through its exit transition, and `loading` lags a render behind.
  const inFlightRef = useRef(false);
  // Clearing the typed prod clears the tick, so retyping it does not re-arm
  // Create on its own.
  useEffect(() => {
    if (confirmProductionSend !== true) {
      setAcknowledgedKey(null);
    }
  }, [confirmProductionSend]);

  const handleCountChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setCountText(event.target.value);
    },
    [],
  );
  const handleShapeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setShape(event.target.value as BenchTenderShape);
    },
    [],
  );
  const handleAcknowledgeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setAcknowledgedKey(
        event.target.checked && partnershipUuid !== null && count !== null
          ? acknowledgementKey(partnershipUuid, count)
          : null,
      );
    },
    [count, partnershipUuid],
  );
  const handleCancelConfirm = useCallback(() => {
    setConfirmOpen(false);
  }, []);
  const sendCreate = useCallback(() => {
    if (inFlightRef.current || partnershipUuid === null || count === null) {
      return;
    }
    inFlightRef.current = true;
    setConfirmOpen(false);
    setMessage(null);
    setStaleNotice(null);
    setLaneWarning(null);
    setSideEffects([]);
    setError(null);
    const requestedFor = partnershipUuid;
    createOrders({
      variables: {
        input: {
          ediPartnershipUuid: requestedFor,
          count,
          shape,
          ...scheduleInput(schedule, shape),
          // Sent only from the confirm dialog, which only production opens.
          ...(production
            ? {
                confirmProductionSend: true,
                confirmProductionOrderCreation: true,
              }
            : {}),
        },
      },
    })
      .then((result) => {
        throwOnGraphqlErrors(result);
        const created = result.data?.createOutboundBenchOrders;
        if (created === undefined) {
          throw new Error("The mutation returned no result.");
        }
        const names = created.orders.map((order) => order.name).join(", ");
        if (currentPartnershipRef.current === requestedFor) {
          if (created.orders.length > 0) {
            onCreated(created.orders);
            setMessage(
              `${createdMessage(created.orders.length, names, created.laneSourceOrderName ?? null)}${serviceNote(created.receivedServiceNames)} They are in Orders, ready to Dry run or Run.`,
            );
          }
          setLaneWarning(created.warning ?? null);
          setSideEffects(
            created.sideEffects.map(
              (entry) => `${entry.kind}: ${entry.detail}`,
            ),
          );
        } else if (created.orders.length > 0) {
          setStaleNotice(
            `Created ${created.orders.length} order(s) for the partnership you had selected: ${names}. You switched partnership before the bench finished creating them, so it left them out of Orders.`,
          );
        }
        setError(created.failure ?? null);
      })
      .catch((createError: unknown) => {
        const text =
          createError instanceof Error
            ? createError.message
            : "Creating orders failed.";
        setError(
          createError instanceof ApolloError
            ? `${text} ${PARTIAL_CREATE_HINT}`
            : text,
        );
      })
      .finally(() => {
        inFlightRef.current = false;
      });
  }, [
    count,
    createOrders,
    onCreated,
    partnershipUuid,
    production,
    schedule,
    shape,
  ]);
  const handleCreate = useCallback(() => {
    if (production) {
      setConfirmOpen(true);
      return;
    }
    sendCreate();
  }, [production, sendCreate]);
  const createReady = canCreateOrders({
    partnershipUuid,
    count,
    production,
    confirmProductionSend,
    acknowledgedKey,
  });

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">Create test orders</Typography>
      <Typography variant="body2">
        Sends new 204 tenders for the partnership through the EDI inbound
        handler, so each order arrives the way a partner&apos;s tender does. In
        production it also needs the typed confirmation above, the
        acknowledgement below and a yes in the confirm dialog.
      </Typography>
      <Box sx={styles.controls}>
        <TextField
          error={count === null}
          helperText={`1 to ${MAX_CREATED_ORDERS}`}
          label="How many"
          sx={styles.count}
          type="number"
          value={countText}
          onChange={handleCountChange}
        />
        <TextField
          select
          label="Stops"
          sx={styles.shape}
          value={shape}
          onChange={handleShapeChange}
        >
          {SHAPES.map((value) => (
            <MenuItem key={value} value={value}>
              {SHAPE_LABELS[value]}
            </MenuItem>
          ))}
        </TextField>
        <Button
          color={production ? "error" : "primary"}
          disabled={!createReady || loading}
          sx={styles.createButton}
          variant="outlined"
          onClick={handleCreate}
        >
          {loading ? "Creating..." : "Create test orders"}
        </Button>
      </Box>
      <CreateOrdersScheduleFields
        partnershipUuid={partnershipUuid}
        schedule={schedule}
        shape={shape}
        onChange={setSchedule}
      />
      {production && (
        <Alert severity="warning">
          <Typography variant="body2">
            This creates real orders in production. The bench refuses a customer
            whose new orders would reach anyone besides Tenedi. Type prod above,
            then tick the box.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  partnershipUuid !== null &&
                  count !== null &&
                  acknowledgedKey === acknowledgementKey(partnershipUuid, count)
                }
                disabled={confirmProductionSend !== true || count === null}
                onChange={handleAcknowledgeChange}
              />
            }
            label={productionAcknowledgement({
              count,
              companyName,
              contactName,
            })}
          />
        </Alert>
      )}
      <Dialog open={confirmOpen} onClose={handleCancelConfirm}>
        <DialogTitle>Create real orders in production?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {count ?? "?"} order(s) for {contactName ?? "the customer"} in{" "}
            {companyName ?? "the company"}, with {SHAPE_LABELS[shape]}. Each
            gets a BENCH- HAWB, and each runs the customer&apos;s order-created
            handling.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleCancelConfirm}>
            Cancel
          </Button>
          <Button color="error" disabled={loading} onClick={sendCreate}>
            Create in production
          </Button>
        </DialogActions>
      </Dialog>
      {sideEffects.length > 0 && (
        <Alert severity="info">
          Creating these orders also reached: {sideEffects.join(" ")}
        </Alert>
      )}
      {message !== null && <Alert severity="success">{message}</Alert>}
      {laneWarning !== null && <Alert severity="warning">{laneWarning}</Alert>}
      {staleNotice !== null && <Alert severity="warning">{staleNotice}</Alert>}
      {error !== null && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
};

export default CreateOrdersSection;
